/* eslint-disable */
// Using a angular-oauth2-oidc library to support OAuth 2 and OpenId Connect (OIDC) in Angular
// https://manfredsteyer.github.io/angular-oauth2-oidc/
// --------------------------------------------------------------------

import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { UserToken } from '../interfaces/user-token.model';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '@/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isBrowser = null;
  private envRedirectURL: string;

  constructor(private oauthService: OAuthService, public zone: NgZone, @Inject(PLATFORM_ID) private platformId: any) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    if (this.isBrowser) {
      this.envRedirectURL = `${window.location.origin}${environment.authConfig?.redirectUri}`;
    }

    this.oauthService.events.subscribe((event) => {
      const errorResponsesRequiringUserInteraction = [
        'interaction_required',
        'login_required',
        'account_selection_required',
        'consent_required',
      ];
      if (event instanceof OAuthErrorEvent) {
        if (
          event.type == 'code_error' &&
          event.params &&
          errorResponsesRequiringUserInteraction.indexOf(event.params['error'])
        ) {
          if (this.isBrowser) {
            this.zone.run(() => {
              sessionStorage.removeItem('id_token');
              sessionStorage.removeItem('access_token');
            });
          }
        }
      } else {
        if (this.isBrowser) {
          if (event.type === 'token_expires') {
            this.zone.run(() => {
              sessionStorage.removeItem('id_token');
              sessionStorage.removeItem('access_token');
              window.location.href = 'https://www.virginmedia.com/my-virgin-media/';
            });
          }
        }
      }
    });
  }

  private isSilentRefreshON = false;

  initialise() {
    if (this.isBrowser) {
      const authConfig = { ...environment.authConfig, redirectUri: this.envRedirectURL };
      this.oauthService.configure(authConfig);
      const urlSearchParams = new URLSearchParams(window.location.search);
      const codeParam = urlSearchParams.get('code');
      if (codeParam) {
        this.loginWithAuthCode();
      } else {
        const errorParam = urlSearchParams.get('error_description');
        if (!errorParam) {
          this.loginOrGetAuthCode();
        }
      }
    }
  }

  loginWithAuthCode() {
    this.oauthService.tryLoginCodeFlow().then((result) => {
      // console.log('loginWithAuthCode -> result: ', result);
    });
  }

  private loginOrGetAuthCode() {
    const returnUrl = decodeURIComponent(this.oauthService.state);
    this.oauthService.loadDiscoveryDocumentAndTryLogin().then((result) => {
      if (result) {
        if (this.isAuthenticated()) {
          this.setUserProfile();
          // Start token refresh
          if (!this.isSilentRefreshON && environment.authConfig.useSilentRefresh) {
            this.isSilentRefreshON = true;
            this.oauthService.stopAutomaticRefresh();
            this.oauthService.setupAutomaticSilentRefresh();
          }
        } else if (sessionStorage?.getItem('vmo2_zeus_auth_initialised') !== 'true') {
          sessionStorage?.setItem('vmo2_zeus_auth_initialised', 'true');
          const startUrl = returnUrl || this.envRedirectURL;
          const isLandingPage = [
            '/auth-deflection',
            'phr/email',
            'phr/sms',
            'phr/agent',
            'phr/direct',
            '/has-settings-changed',
            '/ivr',
            '/bb-reliability',
            '/bb-reliability/expired',
            '/tv-error-codes',
            '/pod-order/expired',
            '/order-remote',
            '/smart-support',
            '/smart-support/pod-order',
          ].some((v) => (window?.location?.pathname || '').includes(v));
          if (
            !isLandingPage &&
            !window?.location?.pathname?.includes('/guide/') &&
            startUrl?.includes('?error_description=') === false &&
            startUrl?.includes('?code=') === false
          ) {
            this.oauthService.customQueryParams = {
              prompt: 'none',
            };
            this.oauthService.initImplicitFlow();
          }
        }
      }
    });
  }

  setUserProfile() {
    const accessToken: UserToken = {
      value: this.oauthService.getAccessToken(),
      expiry: this.oauthService.getAccessTokenExpiration(),
    };
    const idToken: UserToken = {
      value: this.oauthService.getIdToken(),
      expiry: this.oauthService.getIdTokenExpiration(),
    };
  }

  displayName(): string {
    return this.getIdentityClaimProperty('displayName');
  }

  getIdentityClaimProperty(name: string): string {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) {
      return null;
    }
    /* eslint-disable @typescript-eslint/dot-notation */
    return claims[name];
  }

  get idToken(): string {
    return this.oauthService.getIdToken();
  }

  get accessToken(): string {
    return this.oauthService.getAccessToken();
  }

  get oauthEvents(): any {
    return this.oauthService.events;
  }

  isAuthenticated() {
    return (
      this.oauthService.getIdToken() !== null &&
      this.oauthService.getAccessToken() !== null &&
      this.oauthService.hasValidAccessToken()
    );
  }

  redirectToLogin(returnUrl?: string) {
    if (returnUrl && returnUrl.includes('?code=')) {
      return;
    }
    this.oauthService.initImplicitFlow(returnUrl);
  }

  logout(logoutRedirectUrl?: string) {
    const logoutUrl = `${environment.authConfig?.issuer}/idp/startSLO.ping?TargetResource=${encodeURIComponent(
      logoutRedirectUrl ? logoutRedirectUrl : this.envRedirectURL
    )}`;

    this.oauthService.logOut();
    window.location.href = logoutUrl;
  }
}
